import React from 'react';
import { Card, Space, Tag, Typography, Timeline, Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ClockCircleOutlined, CheckCircleOutlined, AudioOutlined, FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';

const { Text } = Typography;

const MessageDetails = ({ message }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    if (!message) return null;


    const themeColors = {
        cardBackground: isDarkMode ? '#1f1f1f' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)',
        timelineDot: isDarkMode ? '#fff' : undefined,
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        return format(parseISO(dateString), 'HH:mm');
    };

    const formatFullTime = (dateString) => {
        if (!dateString) return '';
        return format(parseISO(dateString), 'PPpp');
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title={t('basic_information')} style={{ background: themeColors.cardBackground }}>
                <Space direction="vertical">
                    <Text style={{ color: themeColors.text }}>{t('id')}: {message.id}</Text>
                    <Text style={{ color: themeColors.text }}>{t('sender_type')}: {message.sender_type}</Text>
                    <Text style={{ color: themeColors.text }}>{t('sent_at')}: {formatFullTime(message.sent_at)}</Text>
                    {message.requires_escalation && <Tag color="red">{t('needs_escalation')}</Tag>}
                    {message.is_abuse && <Tag color="red">{t('marked_as_abusive')}</Tag>}
                </Space>
            </Card>

            {message.error_type && message.error_details && (
                <Card 
                    title={<span style={{ color: isDarkMode ? 'orange' : '#ff4d4f' }}>{t('error_information')}</span>} 
                    style={{ background: themeColors.cardBackground }}
                >
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text }}>{t('error_type')}: {message.error_type}</Text>
                        <Text style={{ color: themeColors.text }}>{t('error_details')}: {message.error_details}</Text>
                    </Space>
                </Card>
            )}

            {message.content && (
                <Card title={t('content')} style={{ background: themeColors.cardBackground }}>
                    <Text style={{ color: themeColors.text }}>{message.content}</Text>
                </Card>
            )}

            {message.agent_analysis && (
                <Card title={t('agent_analysis')} style={{ background: themeColors.cardBackground }}>
                    <Text style={{ color: themeColors.text }}>{message.agent_analysis}</Text>
                </Card>
            )}

            {message.abuse_info && (
                <Card title={t('abuse_information')} style={{ background: themeColors.cardBackground }}>
                    <Text style={{ color: themeColors.text }}>{message.abuse_info}</Text>
                </Card>
            )}

            {message.transaction_info && (
                <Card title={t('transaction_information')} style={{ background: themeColors.cardBackground }}>
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text }}>{t('coins_amount')}: {message.transaction_info.coins_amount}</Text>
                        <Text style={{ color: themeColors.text }}>{t('transaction_id')}: {message.transaction_info.transaction_id}</Text>
                    </Space>
                </Card>
            )}

            {message.escalation && (
                <Card title={t('escalation_details')} style={{ background: themeColors.cardBackground }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text strong style={{ color: themeColors.text }}>{t('customer_query')}:</Text>
                        <Text style={{ color: themeColors.text }}>{message.escalation.customer_query}</Text>

                        <Space>
                            <Text strong style={{ color: themeColors.text }}>{t('priority')}:</Text>
                            <Tag color={message.escalation.priority === 'high' ? 'red' : 'orange'}>
                                {message.escalation.priority}
                            </Tag>
                        </Space>

                        <Text strong style={{ color: themeColors.text }}>{t('status')}: {message.escalation.status}</Text>

                        {message.escalation.resolved_at && (
                            <Text style={{ color: themeColors.text }}>
                                {t('resolved_at')}: {formatFullTime(message.escalation.resolved_at)}
                            </Text>
                        )}

                        {message.escalation.resolved_by && (
                            <Text style={{ color: themeColors.text }}>
                                {t('resolved_by')}: {message.escalation.resolved_by.full_name}
                            </Text>
                        )}

                        {message.escalation.admin_response && (
                            <Card type="inner" title={t('admin_response')} style={{ background: themeColors.cardBackground }}>
                                <Space direction="vertical">
                                    <Text style={{ color: themeColors.text }}>{message.escalation.admin_response.content}</Text>
                                    <Text type="secondary" style={{ color: themeColors.secondaryText }}>
                                        {formatFullTime(message.escalation.admin_response.sent_at)}
                                    </Text>
                                </Space>
                            </Card>
                        )}

                        {message.escalation.agent_response && (
                            <Card type="inner" title={t('agent_response')} style={{ background: themeColors.cardBackground }}>
                                <Space direction="vertical">
                                    <Text style={{ color: themeColors.text }}>{message.escalation.agent_response.content}</Text>
                                    <Text type="secondary" style={{ color: themeColors.secondaryText }}>
                                        {formatFullTime(message.escalation.agent_response.sent_at)}
                                    </Text>
                                </Space>
                            </Card>
                        )}
                    </Space>
                </Card>
            )}

            {message.medias && message.medias.length > 0 && (
                <Card title={t('media')} style={{ background: themeColors.cardBackground }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {message.medias.map((media, index) => (
                            <Card key={index} size="small" style={{ background: themeColors.cardBackground }}>
                                {media.media_type === 'audio' && (
                                    <Space>
                                        <AudioOutlined />
                                        <Space direction="vertical">
                                            <Text style={{ color: themeColors.text }}>{t('duration')}: {media.data?.duration}s</Text>
                                            <Text style={{ color: themeColors.text }}>{t('progress_status')}: {media.processing_status}</Text>
                                            <audio controls src={media.url} style={{ width: '100%' }} />
                                        </Space>
                                    </Space>
                                )}
                                {media.media_type === 'image' && (
                                    <Space direction="vertical">
                                        <div style={{
                                            background: isDarkMode ? '#f0f0f0' : '#ffffff',
                                            padding: 4,
                                            borderRadius: 4,
                                            display: 'inline-block'
                                        }}>
                                            <img
                                                src={media.url}
                                                alt="Message attachment"
                                                style={{ maxWidth: '100%', borderRadius: 4 }}
                                            />
                                        </div>
                                        <Text style={{ color: themeColors.text }}>{t('dimensions')}: {media.data.width}x{media.data.height}</Text>
                                        <Text style={{ color: themeColors.text }}>{t('progress_status')}: {media.processing_status}</Text>
                                        {media.data && media.data.content_description && (
                                            <div style={{
                                                marginTop: 4,
                                                padding: 8,
                                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                                borderRadius: 4,
                                                position: 'relative'
                                            }}>
                                                <Text
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '13px',
                                                        color: themeColors.secondaryText,
                                                        lineHeight: 1.4,
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 500 }}>{t('ai_image_analysis')}:</span> {media.data.content_description}
                                                </Text>
                                                <Tooltip
                                                    title={t('ai_description_tooltip')}
                                                    placement="topRight"
                                                >
                                                    <span style={{
                                                        position: 'absolute',
                                                        insetInlineEnd: 4,
                                                        top: 4,
                                                        fontSize: '9px',
                                                        cursor: 'help',
                                                        color: themeColors.secondaryText
                                                    }}>
                                                        🤖
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </Space>
                                )}
                                {(media.media_type === 'document' || media.media_type === 'pdf') && (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Space>
                                            {media.media_type === 'pdf' ? 
                                                <FilePdfOutlined style={{ fontSize: 24, color: isDarkMode ? '#ff6b6b' : '#ff4d4f' }} /> : 
                                                <FileOutlined style={{ fontSize: 24, color: isDarkMode ? '#52c1ff' : '#1890ff' }} />
                                            }
                                            <Text style={{ color: themeColors.text }}>{media.filename || t('document')}</Text>
                                        </Space>
                                        
                                        <Button 
                                            type="primary" 
                                            size="small"
                                            onClick={() => window.open(media.url, '_blank')}
                                            style={{ marginTop: 8 }}
                                        >
                                            {t('view_document')}
                                        </Button>
                                        
                                        {media.processing_status && (
                                            <Text style={{ color: themeColors.text }}>{t('progress_status')}: {media.processing_status}</Text>
                                        )}
                                    </Space>
                                )}
                            </Card>
                        ))}
                    </Space>
                </Card>
            )}

            <Card title={t('status')} style={{ background: themeColors.cardBackground }}>
                <Timeline
                    items={[
                        {
                            dot: <ClockCircleOutlined style={{ color: themeColors.timelineDot }} />,
                            children: `${t('sent_at')} ${formatTime(message.sent_at)}`
                        },
                        message.status.delivered && {
                            dot: <CheckCircleOutlined style={{ color: themeColors.timelineDot }} />,
                            children: `${t('delivered_at')} ${formatTime(message.status.delivered_at)}`
                        },
                        message.status.read && {
                            dot: <CheckCircleOutlined style={{ color: themeColors.timelineDot }} />,
                            children: `${t('read_at')} ${formatTime(message.status.read_at)}`
                        }
                    ].filter(Boolean)}
                />
            </Card>

            {message.reactions?.length > 0 && (
                <Card title={t('reactions')} style={{ background: themeColors.cardBackground }}>
                    <Space direction="vertical">
                        {message.reactions.map((reaction, index) => (
                            <Space key={index}>
                                <Tag>{reaction.emoji}</Tag>
                                <Text style={{ color: themeColors.text }}>{reaction.sender_type}</Text>
                                <Text type="secondary" style={{ color: themeColors.secondaryText }}>{formatTime(reaction.sent_at)}</Text>
                            </Space>
                        ))}
                    </Space>
                </Card>
            )}

            {message.edit_history && (
                <Card title={t('edit_history')} style={{ background: themeColors.cardBackground }}>
                    <Timeline>
                        {message.edit_history.map((edit, index) => (
                            <Timeline.Item key={index} >
                                <Text style={{ color: themeColors.text }}>"{edit.content}"</Text>
                                <br />
                                <Text type="secondary" style={{ color: themeColors.secondaryText }}>{formatFullTime(edit.edited_at)}</Text>
                            </Timeline.Item>
                        ))}
                        <Timeline.Item>
                            <Text style={{ color: themeColors.text }}>"{message.content}" ({t('current')})</Text>
                        </Timeline.Item>
                    </Timeline>
                </Card>
            )}

            {message.reply_to && (
                <Card title={t('replying_to')} style={{ background: themeColors.cardBackground }}>
                    <Space direction="vertical">
                        <Text style={{ color: themeColors.text }}>{message.reply_to.content}</Text>
                        <Text type="secondary" style={{ color: themeColors.secondaryText }}>
                            {t('by')} {message.reply_to.sender_type} {t('at')} {formatFullTime(message.reply_to.sent_at)}
                        </Text>
                    </Space>
                </Card>
            )}
        </Space>
    );
};

export default MessageDetails;
